/**
 * Password Gate
 * Fullscreen password overlay for the pitch deck
 */

import React, { useState, useRef, useEffect } from 'react';
import { trackDeckGateAttempt } from '@logging';
import type { DeckEdition } from '@components/press/pitch-deck/pitch-deck.types';
import {
  GateContainer,
  GateCard,
  GateLogo,
  GateTitle,
  GateSubtitle,
  GateForm,
  GateInput,
  GateButton,
  ErrorMessage,
} from '@components/press/pitch-deck/password-gate.styles';

interface PasswordGateProps {
  meta: DeckEdition['meta'];
  passwords: DeckEdition['passwords'];
  onSuccess: (watermark: string) => void;
}

/* The edition's own keys only. A plain lookup also finds what every object
   inherits: "constructor" and "__proto__" opened the deck, watermarked
   "[object Object]". */
function watermarkFor(passwords: DeckEdition['passwords'], typed: string): string | undefined {
  const key = typed.toLowerCase().trim();
  return Object.prototype.hasOwnProperty.call(passwords, key) ? passwords[key] : undefined;
}

export const PasswordGate: React.FC<PasswordGateProps> = ({ meta, passwords, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [errorKey, setErrorKey] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const watermark = watermarkFor(passwords, password);

    /* This gate — not the route — is what "opened the deck" means. A
       `web_page_viewed` on /press/blueprint_april_2026 only says someone
       reached the door; most visits stop here. A failed attempt is signal
       too: it usually means a recipient has the link but not the password,
       which is a sharing problem nobody would otherwise hear about.

       On success the watermark travels with the event, so the dashboards can
       answer "did this recipient open the deck?" — the question the deck is
       instrumented for. The password that produced it does not: the two name
       the same recipient, so nothing is lost, but a password in the sink is a
       working key to the deck for anyone who can read logs.

       On failure nothing typed is reported. A string entered into a password
       box cannot be assumed to belong to this system, and a typo would
       otherwise put somebody's unrelated real credential into BigQuery for
       good. See trackDeckGateAttempt for the full rule. */
    if (watermark !== undefined) {
      trackDeckGateAttempt('succeeded', watermark);
    } else {
      trackDeckGateAttempt('failed');
    }

    if (watermark !== undefined) {
      onSuccess(watermark);
    } else {
      setError(true);
      setErrorKey(prev => prev + 1);
      setPassword('');
      inputRef.current?.focus();
    }
  };

  return (
    <GateContainer>
      <GateCard key={errorKey} $hasError={error}>
        <GateLogo>HC</GateLogo>
        <GateTitle>{meta.title}</GateTitle>
        <GateSubtitle>{meta.subtitle} &middot; {meta.date}</GateSubtitle>
        <GateForm onSubmit={handleSubmit}>
          <GateInput
            ref={inputRef}
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              setError(false);
            }}
            autoComplete="off"
          />
          <GateButton type="submit">View Deck</GateButton>
          {error && <ErrorMessage>Incorrect password. Please try again.</ErrorMessage>}
        </GateForm>
      </GateCard>
    </GateContainer>
  );
};
